import { commonUtils } from '@/helpers/commonUtils'
import { envHelper } from '@/helpers/envHelper'

export type MethodType = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
interface ReqInfo {
  url: string
  method: MethodType
  headers: Record<string, string>
  bodyArgs: APIBodyArgCollection
  camelize: boolean
  controller?: AbortController
  timeout?: number
}

type APIQueryArgCollection = ApiTypes.APIQueryArgCollection
type APIBodyArgCollection = ApiTypes.APIBodyArgCollection

// 自定义超时错误
class RequestTimeoutError extends Error {
  constructor(timeout: number) {
    super(`请求超时 - ${timeout}ms`)
    this.name = 'RequestTimeoutError'
  }
}

export class ApiProxy {
  private defaultTimeout = 1000 * 60 // 默认1分钟超时
  // private defaultTimeout = 1000 * 10

  public async sendRequest(
    method: MethodType,
    pathPrefix: string,
    urlArgs: APIQueryArgCollection = {},
    bodyArgs: APIBodyArgCollection = {},
    camelize = true,
    controller?: AbortController,
    timeout: number = this.defaultTimeout
  ): Promise<any> {
    let path = ''
    if (path.indexOf('http') === 0) {
      path = commonUtils.underlizeKey(path)
    } else {
      path = `${envHelper.apiBasePath}${commonUtils.underlizeKey(pathPrefix)}`
    }
    const url = commonUtils.genApiUrl(path, commonUtils.underlize(urlArgs || {}))
    const headers = await this.getAuthHeaders()
    return this.handleSendRequest({
      url,
      headers,
      method,
      bodyArgs,
      camelize,
      controller,
      timeout,
    })
  }

  private async handleSendRequest(reqInfo: ReqInfo): Promise<any> {
    // 创建一个新的AbortController，用于超时控制
    const timeoutController = new AbortController()

    // 如果外部传入了controller，需要同时处理两个controller的信号
    const controller = reqInfo.controller
    const signal = controller ? controller.signal : timeoutController.signal

    // 设置超时定时器
    const timeoutId = setTimeout(() => {
      timeoutController.abort()
    }, reqInfo.timeout || this.defaultTimeout)

    const body: RequestInit = {
      method: reqInfo.method,
      headers: reqInfo.headers,
      signal, // 使用合并后的signal
    }
    if (reqInfo.method === 'POST') {
      body.body = JSON.stringify(commonUtils.underlize(reqInfo.bodyArgs || {}))
    }
    const req = fetch(reqInfo.url, body)
    return req
      .then((res) => {
        clearTimeout(timeoutId)
        if (res.status >= 200 && res.status < 300) {
          return res.json()
        }
        throw commonUtils.camelize(res)
      })
      .then((res) => {
        if (res.success) {
          if (reqInfo.camelize) {
            return commonUtils.camelize(res)
          }
          return res
        }
        throw commonUtils.camelize(res)
      })
      .catch((err) => {
        clearTimeout(timeoutId)
        if (err instanceof RequestTimeoutError) {
          throw new Error('请求超时')
        }
        throw err
      })
  }

  private async getAuthHeaders(): Promise<{ [key: string]: string }> {
    return {}
  }
}
